import React from 'react';
import { Badge } from './ui/badge';
import { Shield, Building, Users } from 'lucide-react';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

export const TrustedBySection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ delay: 0 });
  const [partnersRef, partnersVisible] = useScrollAnimation({ delay: 200 });

  const partners = [
    {
      name: "Ministry of Transportation",
      type: "Government Agency",
      icon: Shield,
      color: "blue"
    },
    {
      name: "Muscat Municipality",
      type: "Municipality",
      icon: Building,
      color: "green"
    },
    {
      name: "Royal Oman Police",
      type: "Law Enforcement",
      icon: Users,
      color: "purple"
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        dot: "bg-blue-500",
        card: "border-blue-200/50 hover:border-blue-300",
        text: "text-blue-600",
        icon: "text-blue-500"
      },
      green: {
        dot: "bg-green-500",
        card: "border-green-200/50 hover:border-green-300",
        text: "text-green-600",
        icon: "text-green-500"
      },
      purple: {
        dot: "bg-purple-500",
        card: "border-purple-200/50 hover:border-purple-300",
        text: "text-purple-600",
        icon: "text-purple-500"
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-10 right-10 w-64 h-64 bg-blue-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-green-200/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div ref={headerRef} className={`text-center mb-12 ${createScrollAnimationClasses('fadeInUp', headerVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-100 to-gray-200 px-6 py-3 rounded-full mb-6 border border-gray-200/50">
            <Shield size={16} className="text-gray-600" />
            <Badge className="bg-transparent text-gray-700 px-0 py-0 text-sm font-semibold">
              Trusted Partners
            </Badge>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Trusted by Government Agencies and Municipalities Across Oman
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our solutions are trusted by leading government institutions and municipalities 
            across the Sultanate of Oman, delivering reliable and innovative traffic management systems.
          </p>
        </div>

        {/* Partners Grid */}
        <div ref={partnersRef} className={`grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 ${createScrollAnimationClasses('fadeInUp', partnersVisible)}`}>
          {partners.map((partner, index) => {
            const IconComponent = partner.icon;
            const colors = getColorClasses(partner.color);
            
            return (
              <div 
                key={partner.name}
                className={`group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 lg:p-8 border ${colors.card} shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2`}
              >
                {/* Floating Icon */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <IconComponent size={20} className={colors.icon} />
                </div>

                {/* Content */}
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-3 h-3 ${colors.dot} rounded-full flex-shrink-0`}></div>
                  <div>
                    <h3 className={`text-lg lg:text-xl font-bold ${colors.text} group-hover:text-gray-800 transition-colors`}>
                      {partner.name}
                    </h3>
                    <p className="text-sm text-gray-500 font-medium">{partner.type}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed">
                  {partner.name === "Ministry of Transportation" && 
                    "Leading transportation infrastructure development and traffic management across Oman with our advanced ITS solutions."}
                  {partner.name === "Muscat Municipality" && 
                    "Implementing smart city initiatives and intelligent traffic systems to enhance urban mobility and citizen services."}
                  {partner.name === "Royal Oman Police" && 
                    "Utilizing our traffic enforcement and monitoring systems to ensure road safety and efficient law enforcement."}
                </p>

                {/* Bottom Accent */}
                <div className={`absolute bottom-0 left-0 w-full h-1 ${colors.dot} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              </div>
            );
          })}
        </div>

        {/* Additional Stats */}
        <div className="mt-16 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group">
              <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2 group-hover:text-blue-700 transition-colors">15+</div>
              <div className="text-gray-600 font-semibold">Years of Partnership</div>
              <div className="text-sm text-gray-500 mt-1">Long-term collaboration</div>
            </div>
            <div className="group">
              <div className="text-3xl lg:text-4xl font-bold text-green-600 mb-2 group-hover:text-green-700 transition-colors">100%</div>
              <div className="text-gray-600 font-semibold">Project Success Rate</div>
              <div className="text-sm text-gray-500 mt-1">Reliable delivery</div>
            </div>
            <div className="group">
              <div className="text-3xl lg:text-4xl font-bold text-purple-600 mb-2 group-hover:text-purple-700 transition-colors">24/7</div>
              <div className="text-gray-600 font-semibold">Support Available</div>
              <div className="text-sm text-gray-500 mt-1">Continuous service</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
